import { z } from "zod";
import { earlierLabEvidenceSchema } from "./earlier-labs";
import { formUploadMetaSchema } from "./intake-template";
import { measurementReviewStatusSchema } from "./measurement-review";

// TES-949 (TES-941 §INSKICKAT MEDICINSKT UNDERLAG) — the compact card next
// to "Riskflaggor: X": which medical evidence the patient has actually handed
// over, across her WHOLE journey (every F-07 and every CP-06 upload), not
// only the open case. Emelie + Kirill:
//   "För receptövertag är F-07 den primära platsen för receptunderlag, tidigare provsvar, blodtryck och vilopuls."
//   "Vi ska inte visa ”nytt blodprov: svar finns” eller liknande automatiska statusar eftersom vi ännu inte har integration mot Webdoc."
//
// So this is facts only: files that exist, the date and source she gave for
// earlier lab results and the newest blood pressure / pulse reading. No
// status says a result was received, reviewed or is acceptable.

// A file the doctor can open through the access-logged
// employee-api/v1/patients/:id/uploads/:uploadId route.
export const evidenceSummaryUploadSchema = formUploadMetaSchema
  .pick({
    id: true,
    templateKey: true,
    filename: true,
    mimeType: true,
    createdAt: true,
  })
  .extend({
    // TES-1125 — an earlier lab result the doctor has marked assessed: when
    // and by whom. Uploaded is not reviewed. Optional so an older reader of
    // this shape is unaffected; absent on prescription documents.
    assessedAt: z.string().nullable().optional(),
    assessedByName: z.string().nullable().optional(),
  });
export type EvidenceSummaryUpload = z.infer<typeof evidenceSummaryUploadSchema>;

export const evidenceSummarySchema = z.object({
  // "Dokumentation av recept/ordination: uppladdat / inte uppladdat" — an
  // empty list is "inte uppladdat".
  prescriptionDocumentation: z.object({
    uploads: z.array(evidenceSummaryUploadSchema),
  }),
  priorLabResults: z.object({
    uploads: z.array(evidenceSummaryUploadSchema),
    // "Provtagningsdatum och eventuell provkälla", as she gave them in her
    // newest F-07 that carries either. Both optional in the form, so both
    // can be null, and a null date is information: the age is unknown.
    sampledAt: z.string().nullable(),
    source: z.string().nullable(),
    // "Hur gammalt provsvaret är": whole days from the sample date to today.
    // A number, not a verdict: no threshold, no colour (TES-911, Ö-235).
    ageDays: z.number().int().nullable(),
  }),
  // TES-1125 — the four-state status of earlier lab results for a
  // NEW_TREATMENT patient, from her F-02 answer and the files on record.
  // Null on the transfer track (F-07 owns earlier results there); optional
  // so an older reader parses.
  earlierLabs: earlierLabEvidenceSchema.nullable().optional(),
  // "Blodtryck och vilopuls med mätdatum, eller att värden saknas" — the
  // newest standing reading (a corrected one is replaced, not listed), or
  // null when there is none.
  bloodPressurePulse: z
    .object({
      systolic: z.number().int().nullable(),
      diastolic: z.number().int().nullable(),
      pulse: z.number().nullable(),
      measuredAt: z.string(),
      // 'patient' | 'form' | 'clinician' | 'import', and where it was
      // entered ("F-07", "health/trends"); null on older rows.
      source: z.string(),
      sourceSurface: z.string().nullable(),
      // TES-1056 — the occasion, and the doctor's assessment of it.
      // Optional so an older reader of this shape is unaffected.
      sessionId: z.string().optional(),
      reviewStatus: measurementReviewStatusSchema.optional(),
    })
    .nullable(),
});
export type EvidenceSummary = z.infer<typeof evidenceSummarySchema>;
